// The feel probes (gate A2.2): scripted input on the fixed step, one sim step at a time through the runtime, on the
// valley's open line (and the 30° ramp's plateau for the coyote ledge), measuring every row of the feel table
// (content/tables/feel.json) this stream owns. The probes drive the hero's controller input directly (the valley's
// `override`), so a press lands on an exact step; the bots drive the actions instead. The metric IDs are the feel
// table's; tests/feel.spec.ts scores them with @rpg/harness's scoreGates.
import type { Runtime } from "@rpg/runtime";
import { forwardOf, rightOf, wrapAngle } from "@rpg/controller";
import { FEEL_LINE } from "./course";
import type { ValleyInput, ValleyRig } from "./scene";

export type FeelMetrics = Record<string, number | null>;

/** The probe groups and the metrics each measures. */
export const FEEL_PROBES = ["start", "walk", "mid", "stop", "sprint", "turn", "tip", "jump", "air", "coyote", "buffer", "glide"] as const;
export type FeelProbe = (typeof FEEL_PROBES)[number];

const DEG = 180 / Math.PI;
const EAST = Math.PI / 2; // yaw facing +x

export function createFeel(rig: ValleyRig, runtime: Runtime) {
  const dtMs = 1000 * rig.dt;
  const dt = rig.dt;
  const t = rig.tuning.hero;
  const hero = () => rig.hero();
  const speed = (): number => Math.hypot(hero().vel.x, hero().vel.z);

  /** The input for a world direction (x, z) at a tilt, plus buttons. */
  const go = (x: number, z: number, tilt = 1, b: Partial<ValleyInput> = {}): ValleyInput => {
    const yaw = rig.control().cameraYaw;
    const f = forwardOf(yaw);
    const r = rightOf(yaw);
    const m = Math.hypot(x, z) || 1;
    return { stick: { x: ((x * r.x + z * r.z) / m) * tilt, y: ((x * f.x + z * f.z) / m) * tilt }, jump: false, jumpPressed: false, sprint: false, glide: false, glidePressed: false, interact: false, ...b };
  };
  const idle = (b: Partial<ValleyInput> = {}): ValleyInput => go(0, 0, 0, b);
  /** Step `n` times with `inp` (or a function of the step), calling `each` after every step. */
  const steps = (n: number, inp: ValleyInput | ((i: number) => ValleyInput), each?: (i: number) => void | boolean): void => {
    for (let i = 0; i < n; i++) {
      rig.override = typeof inp === "function" ? inp(i) : inp;
      runtime.advance(dtMs, { draw: false });
      if (each?.(i) === true) break;
    }
  };
  /** Stand on the open line facing +x, at rest, and let the world settle one step. */
  const start = (x = FEEL_LINE.x, y?: number, z = FEEL_LINE.z, yaw = EAST): void => {
    rig.warpHero(x, y ?? rig.terrainAt(x, z), z, yaw);
    steps(1, idle());
  };

  const probes: Record<FeelProbe, (m: FeelMetrics) => void> = {
    start(m) {
      start();
      let t90: number | null = null;
      let peak = 0;
      steps(180, go(1, 0), (i) => {
        if (t90 === null && speed() >= 0.9 * t.runSpeed) t90 = (i + 1) * dt;
        peak = Math.max(peak, speed());
      });
      m["run.t90"] = t90;
      m["run.peak"] = peak / t.runSpeed;
      m["analog.run"] = speed();
    },
    walk(m) {
      start();
      let t90: number | null = null;
      steps(180, go(1, 0, t.walkTilt), (i) => {
        if (t90 === null && speed() >= 0.9 * t.walkSpeed) t90 = (i + 1) * dt;
      });
      m["walk.t90"] = t90;
      m["analog.walk"] = speed();
    },
    mid(m) {
      start();
      steps(180, go(1, 0, (t.walkTilt + t.runTilt) / 2));
      m["analog.mid"] = (speed() - t.walkSpeed) / (t.runSpeed - t.walkSpeed);
    },
    stop(m) {
      start();
      steps(180, go(1, 0));
      const x0 = hero().feet.x;
      let stopTime: number | null = null;
      steps(120, idle(), (i) => {
        if (stopTime === null && speed() < 0.1) stopTime = (i + 1) * dt;
      });
      m["run.stopDist"] = hero().feet.x - x0;
      m["run.stopTime"] = stopTime;
    },
    sprint(m) {
      start();
      steps(180, go(1, 0));
      let t90: number | null = null;
      let burst = 0;
      let lastOut = 0;
      steps(240, go(1, 0, 1, { sprint: true }), (i) => {
        const v = speed();
        if (t90 === null && v >= 0.9 * t.sprintSpeed) t90 = (i + 1) * dt;
        if ((i + 1) * dt <= 0.5) burst = Math.max(burst, v);
        if (Math.abs(v / t.sprintSpeed - 1) > 0.03) lastOut = (i + 1) * dt;
      });
      m["sprint.t90"] = t90;
      m["sprint.burst"] = burst / t.sprintSpeed;
      m["sprint.settle"] = lastOut + dt;
      m["sprint.speed"] = speed();
      const x0 = hero().feet.x;
      steps(150, idle());
      m["sprint.stopDist"] = hero().feet.x - x0;
    },
    turn(m) {
      // 90°: running +x, the stick to -z (a right-angle turn), facing to within 10° of the new heading
      start();
      steps(150, go(1, 0));
      const want = Math.PI; // facing -z
      let face: number | null = null;
      let low = Infinity;
      let slip = 0;
      steps(90, go(0, -1), (i) => {
        const h = hero();
        if (face === null && Math.abs(wrapAngle(h.yaw - want)) < 10 / DEG) face = (i + 1) * dt;
        low = Math.min(low, speed());
        if (speed() > 1) slip = Math.max(slip, Math.abs(wrapAngle(h.yaw - Math.atan2(h.vel.x, h.vel.z))) * DEG);
      });
      m["turn90.faceTime"] = face;
      m["turn90.minSpeed"] = low / t.runSpeed;
      m["turn90.slip"] = slip;
      // 180°: running +x, the stick reversed
      start();
      steps(150, go(1, 0));
      const x0 = hero().feet.x;
      let maxX = x0;
      let recover: number | null = null;
      let slip180 = 0;
      steps(150, go(-1, 0), (i) => {
        const h = hero();
        maxX = Math.max(maxX, h.feet.x);
        if (recover === null && -h.vel.x >= 0.9 * t.runSpeed) recover = (i + 1) * dt;
        if (speed() > 1.5) slip180 = Math.max(slip180, Math.abs(wrapAngle(h.yaw - Math.atan2(h.vel.x, h.vel.z))) * DEG);
      });
      m["turn180.recover"] = recover;
      m["turn180.slip"] = slip180;
      m["turn180.overshoot"] = maxX - x0;
    },
    tip(m) {
      // from rest facing +x, the stick to -x: turn in place first
      start();
      const p0 = { ...hero().feet };
      let disp: number | null = null;
      let time: number | null = null;
      steps(90, go(-1, 0), (i) => {
        const h = hero();
        const off = Math.abs(wrapAngle(h.yaw + EAST));
        if (disp === null && off < 30 / DEG) disp = Math.hypot(h.feet.x - p0.x, h.feet.z - p0.z);
        if (time === null && off < 10 / DEG) time = (i + 1) * dt;
      });
      m["tip.disp"] = disp;
      m["tip.time"] = time;
      // a 60° stick from rest sets off at once
      start();
      const p1 = { ...hero().feet };
      steps(Math.round(0.15 / dt), go(Math.cos(60 / DEG), -Math.sin(60 / DEG)));
      m["tip.small"] = Math.hypot(hero().feet.x - p1.x, hero().feet.z - p1.z);
    },
    jump(m) {
      const jumpOnce = (held: boolean): { apex: number; tApex: number; air: number } => {
        start();
        const y0 = hero().feet.y;
        let apex = 0;
        let tApex = 0;
        let air = 0;
        let left = false;
        steps(150, (i) => idle({ jump: held || i === 0, jumpPressed: i === 0 }), (i) => {
          const h = hero();
          if (h.feet.y - y0 > apex) {
            apex = h.feet.y - y0;
            tApex = (i + 1) * dt;
          }
          if (h.mode !== "ground") left = true;
          if (left && h.mode === "ground") {
            air = (i + 1) * dt;
            return true;
          }
        });
        return { apex, tApex, air };
      };
      const full = jumpOnce(true);
      const tap = jumpOnce(false);
      m["jump.apex"] = full.apex;
      m["jump.tApex"] = full.tApex;
      m["jump.air"] = full.air;
      m["jump.fallRatio"] = (full.air - full.tApex) / full.tApex;
      m["jump.minApex"] = tap.apex / full.apex;
    },
    air(m) {
      /** Run (or stand), jump, hold `stick` from take-off; return speeds and travel at landing. */
      const leap = (run: boolean, stick: ValleyInput): { v0: number; vx0: number; vLand: number; vxLand: number; travel: number; keep: number } => {
        let xLand = 0;
        start();
        if (run) steps(150, go(1, 0));
        const v0 = speed();
        const vx0 = hero().vel.x;
        const x0 = hero().feet.x;
        let left = false;
        let vLand = 0;
        let vxLand = 0;
        let landed = -1;
        let keep = 0;
        steps(200, (i) => (i === 0 ? { ...stick, jump: true, jumpPressed: true } : { ...stick, jump: true }), (i) => {
          const h = hero();
          if (h.mode !== "ground") left = true;
          if (left && landed < 0 && h.mode === "ground") {
            landed = i;
            vLand = speed();
            vxLand = h.vel.x;
            xLand = h.feet.x;
          }
          if (landed >= 0 && i - landed === Math.round(0.1 / dt)) {
            keep = speed();
            return true;
          }
        });
        return { v0, vx0, vLand, vxLand, travel: xLand - x0, keep };
      };
      const carry = leap(true, go(1, 0));
      m["jump.carry"] = carry.vLand / carry.v0;
      m["land.keepSpeed"] = carry.keep / t.runSpeed;
      const steer = leap(false, go(1, 0));
      // travel by landing: the 0.1 s after landing is ground running, so measure to the landing instead
      m["jump.airSteer"] = steer.travel;
      const rev = leap(true, go(-1, 0));
      m["jump.airReverse"] = rev.vxLand / rev.vx0;
    },
    coyote(m) {
      // run off the 30° ramp's plateau (1.2 m up) to +x; a jump pressed j steps after leaving the ground
      const ramp = rig.course.ramps.find((r) => r.degrees === 30)!;
      const trial = (j: number | null): { left: number; jumped: boolean } => {
        rig.warpHero(ramp.x - 0.6, ramp.height, ramp.topZ, EAST);
        steps(1, idle());
        let left = -1;
        let jumped = false;
        steps(90, (i) => go(1, 0, 1, left >= 0 && j !== null && i === left + j ? { jump: true, jumpPressed: true } : {}), (i) => {
          const h = hero();
          if (left < 0 && !h.grounded) left = i;
          if (h.vel.y > 1) jumped = true;
        });
        return { left, jumped };
      };
      let window = 0;
      for (let j = 1; j <= 40; j++) {
        if (!trial(j).jumped) break;
        window = j * dt;
      }
      m["coyote.window"] = window;
    },
    buffer(m) {
      // fall from 3 m onto the open line; a jump pressed k steps before the landing step
      const drop = (k: number | null, landing: number): { land: number; jumped: boolean } => {
        start(FEEL_LINE.x, rig.terrainAt(FEEL_LINE.x, FEEL_LINE.z) + 3);
        let land = -1;
        let jumped = false;
        steps(150, (i) => idle(k !== null && i === landing - k ? { jump: true, jumpPressed: true } : {}), (i) => {
          const h = hero();
          if (land < 0 && h.mode === "ground") land = i;
          if (land >= 0 && h.vel.y > 1) jumped = true;
        });
        return { land, jumped };
      };
      const landing = drop(null, -1).land;
      let window = 0;
      for (let k = 1; k <= 40; k++) {
        if (!drop(k, landing).jumped) break;
        window = k * dt;
      }
      m["buffer.window"] = window;
    },
    glide(m) {
      // 12 m up over the open line: fall, open the glide, hold the stick +x for 3 s
      start(FEEL_LINE.x + 2, rig.terrainAt(FEEL_LINE.x + 2, FEEL_LINE.z) + 12);
      steps(30, idle());
      steps(1, go(1, 0, 1, { glide: true, glidePressed: true }));
      steps(Math.round(3 / dt), go(1, 0, 1, { glide: true }));
      const h = hero();
      m["glide.sink"] = h.mode === "glide" ? -h.vel.y : null;
      m["glide.speed"] = h.mode === "glide" ? speed() : null;
    },
  };

  return {
    /** Run the probes (all by default) and return the metrics by feel-table ID. Leaves the hero at the hub, idle. */
    measure(names: readonly FeelProbe[] = FEEL_PROBES): FeelMetrics {
      const m: FeelMetrics = {};
      try {
        for (const n of names) probes[n](m);
      } finally {
        rig.override = null;
      }
      return m;
    },
  };
}
